import {
  useCategoriesV2,
  useEvent,
  useTicketsV2,
  type CategoryResponse,
  type EventResponse,
  type TicketResponse,
  CategoryType,
  DateVisibility,
} from "@xs2event/js-lib/api";
import { camelToSnake, defineContext } from "@xs2event/js-lib/util";
import { computed, type FunctionalComponent, ref, type Ref, watch } from "vue";
import { storeToRefs } from "pinia";
import NumberedSeatingIcon from "~icons/material-symbols/counter-1-outline";
import RoofingIcon from "~icons/material-symbols/roofing-outline";
import ScreenIcon from "~icons/material-symbols/desktop-windows-outline";
import { categoryTypeMeta, categoryTypeRank } from "./categoryTypeMeta";
import { useRouteQuery } from "@vueuse/router";
import { formatDays } from "@xs2event/js-lib";
import { Xs2eventVatRegisteredCountries } from "@xs2event/js-lib";
import brandconfig, { isB2C } from "@/brandconfig";
import { setDescription, setTitle, updateOrCreateSchema } from "@m-media/vue3-meta-tags";
import { t } from "@xs2event/js-lib/util";
import dayjs from "dayjs";
import { useUserStore } from "@/store/user.js";
import { iso3ToIso2 } from "@/helpers/format.js";
import { getPath } from "@/components/event";
import { useRouter } from "vue-router";
import { useTicket } from "@/helpers/tickets.js";
import { suppliersDistributorsVATFilterOn } from "@/constants";
import { usePrefetchTeamsForEvents } from "@/helpers/teams";

// Key of whichever perk has `impliedByCategoryType: true` (currently grandstand/hospitality/standing,
// but not restricted to those — any perk key can be wired up via categoryTypeFilterRef).
export type CategoryTypeFilterKey = string;

export type Category = CategoryResponse & {
  tickets: TicketResponse[];
  minPrice: number;
  stock: number;
  subcategory?: string;
  isMultipleDays: boolean;
  subcategoryName?: string;
  ticketOptions: ({ amount: number; netRate: number } & TicketResponse)[];
  cheapestTicket?: { amount: number; netRate: number } & TicketResponse;
};

const [provideEventContext, useEventContext] = defineContext((eventId: string) => {
  const router = useRouter();
  const eventsQuery = useEvent({ eventId });
  const { data: event } = eventsQuery;
  const userStore = useUserStore();
  const { foreign_vat_numbers: foreignVatNumbers, member_state: memberState } = storeToRefs(userStore);

  const canShowDate = computed(() => {
    if (!event.value) return false;
    return event.value.dateVisibility !== DateVisibility.Hidden;
  });

  const showTime = computed(() => {
    if (!event.value) return false;
    if (["formula1", "motogp"].includes(event.value.sportType)) return false;
    if (isMultipleDays.value) return false;
    if (event.value.dateVisibility === DateVisibility.ShowDateOnly) return false;
    if (event.value.dateVisibility === DateVisibility.Hidden) return false;
    return true;
  });

  // Key for storing query parameters from events page
  const EVENTS_QUERY_STORAGE_KEY = "events-page-query-params";

  const categoriesQuery = useCategoriesV2(
    computed(() => ({ eventId: event.value?.eventId })),
    { pageSize: 9999, enabled: computed(() => !!event.value) }
  );

  // Fetch both teams in one go (in:[] query) so EventTitle and others read from cache
  const teamsQuery = usePrefetchTeamsForEvents(
    computed(() => (event.value ? [event.value] : [])),
    {
      enabled: computed(() => !!event.value?.eventId && (!!event.value?.hometeamId || !!event.value?.visitingId)),
    }
  );
  const prefetchedTeams = teamsQuery.data;
  const prefetchedTeamsLoading = computed(() => teamsQuery.isPending.value);

  const selectedCategory = useRouteQuery<string | undefined>("category", undefined, { mode: "push" });
  const selectedSubcategory = useRouteQuery<string | undefined>("sub-category", undefined, { mode: "push" });
  const sort = useRouteQuery<"alphabetical" | "price">("sort", "price", { mode: "push" });

  // Shared by CategoriesHeader.vue (the Filters button) and the Mobile views (the Filters overlay)
  const filtersOpen = useRouteQuery<1 | undefined>("filter-open", undefined, { mode: "push" });

  // Single source of truth for the categoryType filter (Grandstand/Hospitality/General Admission), shared by
  // the Perks switches in Filters.vue and the categoryType tab bar in CategoriesList.vue.
  const selectedCategoryType = useRouteQuery<CategoryTypeFilterKey | undefined>("category-type", undefined, {
    mode: "push",
  });

  // Writable Ref<number> view of selectedCategoryType for a given perk key, so it slots into the
  // existing perks[key].filtered interface (Switches, filterCount, the perks AND-filter) unchanged.
  const categoryTypeFilterRef = (key: CategoryTypeFilterKey) =>
    computed<number>({
      get: () => (selectedCategoryType.value === key ? 1 : 0),
      set: (value) => {
        selectedCategoryType.value = value ? key : undefined;
      },
    });

  const xs2eventVatRegisteredValues = Xs2eventVatRegisteredCountries.map((item) => item.value);

  const filterTicketsWithoutVat = useRouteQuery<number>(
    "vat-filter",
    (suppliersDistributorsVATFilterOn.includes(userStore.supplier_id) ||
      suppliersDistributorsVATFilterOn.includes(userStore.distributor_id)) &&
      !!event.value?.isoCountry &&
      xs2eventVatRegisteredValues.includes(event.value.isoCountry)
      ? 1
      : 0,
    { mode: "push" }
  );
  const showOnlyAvailableTicekts = useRouteQuery<number>("show-all-tickets", 1, {
    mode: "replace",
    transform: (value) => {
      if (typeof value === "number") return value;
      if (value == null || value === "") return 1;
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 1 : parsed;
    },
  });

  const isSingleDayEvent = computed(() => {
    if (!event.value) return false;
    const numberOfDays =
      (Date.parse(event.value.dateStop + "Z") - Date.parse(event.value.dateStart + "Z")) / (1000 * 3600 * 24);
    return numberOfDays <= 1;
  });

  const isMultipleDays = computed(() => {
    if (!event.value) return false;
    const numberOfDays =
      (Date.parse(event.value.dateStop + "Z") - Date.parse(event.value.dateStart + "Z")) / (1000 * 3600 * 24);
    return numberOfDays > 1;
  });

  const filterCount = computed(
    () =>
      Object.values(perks).filter((x) => x.filtered.value == 1).length +
      (filterTicketsWithoutVat.value ? 1 : 0) +
      (selectedDayDurations.value.length > 0 ? 1 : 0)
  );

  const guests = useRouteQuery<number | undefined>("guests", undefined, {
    transform: (x) => (typeof x !== "undefined" ? Number(x) : undefined),
  });

  const selectedDayDurations = useRouteQuery<string[]>("day-durations", [], {
    mode: "push",
    transform: {
      from: (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(Boolean);
        if (typeof value === "string") return value.split(",").filter(Boolean);
        return [];
      },
      to: (value) => {
        if (!Array.isArray(value) || value.length === 0) return undefined;
        return value.filter(Boolean);
      },
    },
  });

  const totalTicketAmount = computed(() => {
    return unfilteredCategories.value.reduce((accumulator, category) => {
      const maxAmount =
        category.ticketOptions.length > 0 ? Math.max(...category.ticketOptions.map((x) => x.amount)) : 0;
      return accumulator + maxAmount;
    }, 0);
  });

  watch(
    event,
    (event: EventResponse | undefined) => {
      if (!event) return;

      const {
        tournamentName,
        eventName,
        sportType,
        dateStart,
        dateStop,
        city,
        venueName,
        latitude,
        longitude,
        numberOfTickets,
        minTicketPriceEur,
        hometeamName,
        visitingName,
      } = event;

      setTitle(
        `${t("Official")} ${t(tournamentName)} ${t("tickets for")} ${eventName} | ${brandconfig.BRAND_SHORT_TITLE} ${t("Tickets")}`
      );
      setDescription(
        `${t("Buy your official")} ${t(tournamentName)} ${t("tickets for")} ${eventName} ${t("easy, secure and fast with")} ${brandconfig.BRAND_TITLE} ${t("Tickets")}`
      );
      if (sportType === "formula1") {
        const structuredDataFormula1 = {
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          sport: `${sportType}`,
          name: `${eventName}`,
          description: `${t("Official")} ${t(tournamentName)} ${t("tickets for")} ${eventName} | ${brandconfig.BRAND} ${t("Tickets")}`,
          startDate: `${dateStart}`,
          endDate: `${dateStop}`,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: {
            "@type": "Place",
            name: `${venueName}`,
            address: {
              "@type": "PostalAddress",
              addressLocality: `${city}`,
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: `${latitude}`,
              longitude: `${longitude}`,
            },
          },
          offers: {
            "@type": "Offer",
            url: `${window.location.href}`,
            availability: `${numberOfTickets}`,
            price: `${(minTicketPriceEur || 0) / 100}`,
            priceCurrency: "EUR",
          },
          organizer: {
            "@type": "Organization",
            name: `${venueName}`,
          },
        };
        updateOrCreateSchema(structuredDataFormula1);
      } else {
        const structuredDataTwoTeams = {
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          sport: `${sportType}`,
          homeTeam: {
            "@context": "https://schema.org",
            "@type": "SportsTeam",
            name: `${hometeamName}`,
            sport: `${sportType}`,
            url: `${window.location.host}/events?team=${hometeamName}`,
          },
          awayTeam: {
            "@context": "https://schema.org",
            "@type": "SportsTeam",
            name: `${visitingName}`,
            sport: `${sportType}`,
            url: `${window.location.host}/events?team=${visitingName}`,
          },
          name: `${eventName}`,
          description: `${t("Official")} ${t(tournamentName)} ${t("tickets for")} ${eventName} | ${brandconfig.BRAND} ${t("Tickets")}`,
          startDate: `${dateStart}`,
          endDate: `${dateStop}`,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: {
            "@type": "Place",
            name: `${venueName}`,
            address: {
              "@type": "PostalAddress",
              addressLocality: `${city}`,
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: `${latitude}`,
              longitude: `${longitude}`,
            },
          },
          offers: {
            "@type": "Offer",
            url: `${window.location.href}`,
            availability: `${numberOfTickets}`,
            price: `${(minTicketPriceEur || 0) / 100}`,
            priceCurrency: "EUR",
          },
          performer: [
            {
              "@type": "SportsTeam",
              name: `${hometeamName}`,
              url: `${window.location.host}/events?team=${hometeamName}`,
            },
            {
              "@type": "SportsTeam",
              name: `${visitingName}`,
              url: `${window.location.host}/events?team=${visitingName}`,
            },
          ],
          organizer: {
            "@type": "Organization",
            name: `${venueName}`,
          },
        };
        updateOrCreateSchema(structuredDataTwoTeams);
      }
    },
    { immediate: true }
  );

  const ticketsQuery = useTicketsV2(
    computed(() => ({
      eventId: event.value?.eventId,
      stock: "ge:0",
    })),
    { enabled: computed(() => !!event.value), pageSize: 9999 }
  );

  const fetchingComplete = computed(() => {
    return eventsQuery.isFetched.value && categoriesQuery.isFetched.value && ticketsQuery.isFetched.value;
  });

  const perks = {
    grandstand: {
      label: categoryTypeMeta[CategoryType.Grandstand]!.label,
      filtered: categoryTypeFilterRef("grandstand"),
      icon: categoryTypeMeta[CategoryType.Grandstand]!.icon,
      // Already shown by the categoryType group heading, so redundant as a per-card perk icon
      impliedByCategoryType: true,
      categoryType: CategoryType.Grandstand,
      filterFn: (cat) => {
        return cat.categoryType == CategoryType.Grandstand;
      },
    },
    hospitality: {
      label: categoryTypeMeta[CategoryType.Hospitality]!.label,
      filtered: categoryTypeFilterRef("hospitality"),
      icon: categoryTypeMeta[CategoryType.Hospitality]!.icon,
      // Already shown by the categoryType group heading, so redundant as a per-card perk icon
      impliedByCategoryType: true,
      categoryType: CategoryType.Hospitality,
      filterFn: (cat) => {
        return cat.categoryType == CategoryType.Hospitality || cat.categoryType == CategoryType.OffsiteHospitality;
      },
    },
    coveredArea: {
      label: "Covered seating",
      filtered: useRouteQuery("covered-area", 0),
      icon: RoofingIcon,
      impliedByCategoryType: false,
      filterFn: (cat) => {
        return cat.options.coveredSeat;
      },
    },
    standing: {
      label: categoryTypeMeta[CategoryType.Generaladmission]!.label,
      filtered: categoryTypeFilterRef("standing"),
      icon: categoryTypeMeta[CategoryType.Generaladmission]!.icon,
      // Already shown by the categoryType group heading, so redundant as a per-card perk icon
      impliedByCategoryType: true,
      categoryType: CategoryType.Generaladmission,
      filterFn: (cat) => {
        return cat.categoryType == CategoryType.Generaladmission;
      },
    },
    videoWall: {
      label: "Videowall",
      filtered: useRouteQuery("video-wall", 0),
      icon: ScreenIcon,
      impliedByCategoryType: false,
      filterFn: (cat) => {
        return cat.options.videowall;
      },
    },
    numberedSeating: {
      label: "Numbered seating",
      filtered: useRouteQuery("numbered-seating", 0),
      icon: NumberedSeatingIcon,
      impliedByCategoryType: false,
      filterFn: (cat) => {
        return cat.options.numberedSeat;
      },
    },
  } as const satisfies Record<
    string,
    {
      label: string;
      filtered: Ref<number>;
      icon: FunctionalComponent;
      impliedByCategoryType?: boolean;
      categoryType?: CategoryType;
      filterFn: (category: CategoryResponse) => boolean;
    }
  >;

  /** Perks worth showing as icons on a category card (the categoryType ones are already in the heading) */
  const perkIconsFor = (category: CategoryResponse) =>
    Object.entries(perks)
      .filter(([, perk]) => perk.filterFn(category) && !perk.impliedByCategoryType)
      .map(([key, perk]) => ({ key, ...perk }));

  const { data: tickets } = ticketsQuery;

  // Create reactive tickets using watch to avoid infinite loops
  const reactiveTickets = ref<(TicketResponse & { reserved?: number })[]>([]);

  watch(
    tickets,
    (newTickets) => {
      reactiveTickets.value = newTickets.map((ticket) => ({ ...useTicket(camelToSnake(ticket)), ...ticket }));
    },
    { immediate: true }
  );

  const shouldGroupBySupplier = ref(!isB2C && userStore.isAdmin);

  /**
   * CategoryId that is currently selected via the venue map.
   * Used by desktop views to filter the visible category list for a clicked section.
   */
  const mapSelectedCategoryId = ref<string[]>();

  const createCategoryEntry = (
    category: CategoryResponse,
    subcategory: string,
    _tickets: typeof reactiveTickets.value,
    isMultipleDays: boolean
  ) => {
    const tickets = _tickets.sort((a, b) => {
      // Sort by price
      if (a.localRates.netRateEur! != b.localRates.netRateEur!) {
        return a.localRates.netRateEur! - b.localRates.netRateEur!;
      }

      // If prices are equal, prefer Xs2event suppliers
      return (a.isXs2eventSupplier ? -1 : 1) - (b.isXs2eventSupplier ? -1 : 1);
    });

    const maxStock = Math.max(...tickets.map((t) => t.stock + (t.reserved || 0)));

    const maxAmount = Math.min(maxStock, isB2C ? 10 : 1000);

    //For every possible amount of tickets, find the cheapest one available
    const ticketOptions = Array.from({ length: maxAmount }, (_, i) => i + 1).reduce<
      ({ amount: number; netRate: number } & TicketResponse)[]
    >((accumulator, amount) => {
      for (let i = 0; i < tickets.length; i++) {
        const supplierTicket = tickets[i];

        if (!supplierTicket) continue;

        const available = supplierTicket.stock + (supplierTicket.reserved || 0);
        if (amount > available) continue;

        //Check if supplier doesn't want to offer odd amount of tickets and not 1 ticket left
        if (supplierTicket.flags && supplierTicket.flags.includes("pairs_only") && available != 1 && amount % 2 == 1)
          continue;

        //Check if supplier doesn't want to be left with 1 ticket
        if (supplierTicket.flags?.includes("no_max_minus_1")) {
          // only relevant when there's more than 1 ticket total
          if (available > 1 && amount === available - 1) {
            continue;
          }
        }

        //Ticket is available for this supplier, add it as an option
        accumulator.push({ amount, ...supplierTicket });
        break;
      }

      return accumulator;
    }, []);

    const firstTicket = tickets[0];

    return {
      ...category,
      categoryName: category.categoryName,
      subcategoryName: isMultipleDays
        ? formatDays(firstTicket.ticketValidfrom, firstTicket.ticketValiduntil)
        : undefined,
      isMultipleDays,
      subcategory,
      tickets: tickets,
      minPrice: tickets.reduce(
        (accumulator, value) => (value.netRate < accumulator ? value.netRate : accumulator),
        Infinity
      ),
      ticketOptions: ticketOptions,
      stock: (ticketOptions.length && Math.max(...ticketOptions.map((t) => t.amount))) || 0,
    };
  };

  const allCategories = computed(() => {
    return categoriesQuery.data.value;
  });

  const unfilteredCategories = computed<Category[]>(() => {
    if (!event.value) return [];

    //It can only have subcategories if the event lasts multiple days
    const isMultipleDays = Math.abs(dayjs(event.value.dateStart).diff(dayjs(event.value.dateStop), "day")) > 1;
    return categoriesQuery.data.value.flatMap((category) => {
      const categoryTickets = reactiveTickets.value.filter(
        (ticket) => ticket.categoryId === category.categoryId && ticket.ticketStatus != "disabled"
      );
      // Filter tickets based on VAT settings

      if (shouldGroupBySupplier.value) {
        // group tickets by subcategory and supplier
        const ticketsBySubAndSupplier = categoryTickets.reduce<
          Record<string, Record<string, typeof reactiveTickets.value>>
        >((acc, ticket) => {
          const subKey = ticket.subCategory;
          const supplierKey = ticket.supplierId || "unknown";
          if (!acc[subKey]) acc[subKey] = {};
          if (!acc[subKey][supplierKey]) acc[subKey][supplierKey] = [];
          acc[subKey][supplierKey].push(ticket);
          return acc;
        }, {});

        return Object.entries(ticketsBySubAndSupplier).flatMap(([subcategory, supplierMap]) =>
          Object.entries(supplierMap).map(([supplierKey, _tickets]) =>
            createCategoryEntry(category, `${subcategory}|${supplierKey}`, _tickets, isMultipleDays)
          )
        );
      } else {
        // group tickets by subcategory
        const ticketsBySub = categoryTickets.reduce<Record<string, typeof reactiveTickets.value>>((acc, ticket) => {
          const key = ticket.subCategory;
          if (!acc[key]) acc[key] = [];
          acc[key].push(ticket);
          return acc;
        }, {});

        // create one category entry per subcategory
        return Object.entries(ticketsBySub).map(([subcategory, _tickets]) =>
          createCategoryEntry(category, subcategory, _tickets, isMultipleDays)
        );
      }
    });
  });

  const availableDayDurations = computed(() => {
    if (!isMultipleDays.value) return [];
    const uniqueDayDurations = new Set<string>();
    unfilteredCategories.value.forEach((category) => {
      if (category.subcategoryName) {
        uniqueDayDurations.add(category.subcategoryName);
      }
    });
    return Array.from(uniqueDayDurations).sort();
  });

  // Categories after every filter except the categoryType selection itself, so counts for the
  // categoryType tab bar (CategoriesHeader.vue) stay consistent with what's actually shown, without
  // fluctuating with whichever tab happens to be selected.
  const categoriesBeforeTypeFilter = computed(() =>
    unfilteredCategories.value
      .map((category) => {
        //Filter ticketOptions based on pricerange
        const ticketOptions = category.ticketOptions
          .filter((ticketOption) => {
            const netRateEur = ticketOption.localRates.netRateEur!;
            const minPrice = priceRange.value[0];
            const maxPrice = priceRange.value[1];
            return netRateEur >= minPrice && netRateEur <= maxPrice;
          })
          .filter((ticketOption) => {
            if (filterTicketsWithoutVat.value && foreignVatNumbers.value && event.value) {
              if (
                ticketOption.vatCategory == "none" &&
                (Object.keys(foreignVatNumbers.value).includes(event.value.isoCountry) ||
                  memberState.value === iso3ToIso2(event.value.isoCountry))
              ) {
                return false;
              }
            }
            return true;
          });

        const cheapestTicket = category.ticketOptions.length
          ? category.ticketOptions
              .slice()
              .sort((a, b) => (a.localRates.netRateEur || Infinity) - (b.localRates.netRateEur || Infinity))[0]
          : undefined;

        return {
          ...category,
          ticketOptions,
          cheapestTicket,
          //Change stock based on filters
          stock: (ticketOptions.length && Math.max(...ticketOptions.map((t) => t.amount))) || 0,
        };
      })
      //If guests filter is active, only find categories with that guest count as an option
      .filter((category) =>
        guests.value != undefined ? !!category.ticketOptions.find((x) => x.amount == guests.value) : true
      )
      //Filter based on perks, except the categoryType ones (grandstand/hospitality/standing) - those
      //are applied afterwards, once, in filteredCategories
      .filter((category) =>
        Object.values(perks).every((x) => x.impliedByCategoryType || !x.filtered.value || x.filterFn(category))
      )
      //Filter by day/duration if selections are made
      .filter((category) => {
        if (!isMultipleDays.value || selectedDayDurations.value.length === 0) return true;
        return category.subcategoryName && selectedDayDurations.value.includes(category.subcategoryName);
      })
      //Filter out categories with no stock when showOnlyAvailableTicekts is enabled
      .filter((category) => !showOnlyAvailableTicekts.value || category.stock > 0)
  );

  const filteredCategories = computed(() =>
    categoriesBeforeTypeFilter.value
      //Filter by the selected categoryType tab, if any
      .filter((category) =>
        Object.values(perks)
          .filter((x) => x.impliedByCategoryType)
          .every((x) => !x.filtered.value || x.filterFn(category))
      )
      //Sort based on user preference
      .sort((a, b) => {
        if (sort.value == "alphabetical") {
          return a.categoryName.localeCompare(b.categoryName);
        } else if (sort.value == "price" || !sort.value) {
          return (
            Math.min(...a.ticketOptions.map((t) => t.localRates.netRateEur || 0)) -
            Math.min(...b.ticketOptions.map((t) => t.localRates.netRateEur || 0))
          );
        }
        return 0;
      })
  );

  const visibleCategories = computed(() => {
    if (!mapSelectedCategoryId.value?.length) return filteredCategories.value;
    return filteredCategories.value.filter((cat) => mapSelectedCategoryId.value?.includes(cat.categoryId));
  });

  // A map click pins the list to one section. Any filter change afterwards would intersect with that
  // pin - showing an unchanged or empty list - so drop the pin whenever a filter changes.
  // ponytail: priceRange left out on purpose, the priceRangeMax watch mutates it on refetch and would
  // clear the pin behind the user's back. Add it if slider changes need to drop the pin too.
  watch(
    () => [
      ...Object.values(perks).map((perk) => perk.filtered.value),
      guests.value,
      selectedDayDurations.value,
      filterTicketsWithoutVat.value,
      showOnlyAvailableTicekts.value,
    ],
    () => {
      mapSelectedCategoryId.value = undefined;
    }
  );

  // Group categories by categoryType, then by categoryId within each type
  const groupedCategories = computed(() => {
    const typeGroups: Record<string, typeof visibleCategories.value> = {};

    visibleCategories.value.forEach((cat) => {
      const categoryType = cat.categoryType ?? "";
      if (!typeGroups[categoryType]) {
        typeGroups[categoryType] = [];
      }
      typeGroups[categoryType].push(cat);
    });

    return (
      Object.entries(typeGroups)
        .map(([categoryType, categoryTypeItems]) => {
          const idGroups: Record<string, typeof visibleCategories.value> = {};

          categoryTypeItems.forEach((cat) => {
            if (!idGroups[cat.categoryId]) {
              idGroups[cat.categoryId] = [];
            }
            idGroups[cat.categoryId].push(cat);
          });

          return {
            categoryType: categoryType as CategoryType,
            groups: Object.entries(idGroups).map(([categoryId, items]) => ({
              title: items[0]?.categoryName || categoryId, // Use categoryName as the title, fallback to categoryId
              items,
            })),
          };
        })
        //Hospitality > Grandstand > General Admission, same order as the categoryType filters
        .sort((a, b) => categoryTypeRank(a.categoryType) - categoryTypeRank(b.categoryType))
    );
  });

  const priceRangeMax = computed(() =>
    Math.max(
      unfilteredCategories.value
        .map((category) => category.ticketOptions.map((t) => t.localRates.netRateEur || 0))
        .flat(1)
        .reduce((a, b) => Math.max(a, b), 0),
      100
    )
  );

  const backTo = () => {
    if (selectedCategory.value) {
      selectedCategory.value = undefined;
      return;
    }

    if (!event.value) {
      return router.go(-1);
    }

    const paths = getPath(camelToSnake(event.value));

    //TODO: Is there no cleaner way to do this?
    const path = Array.isArray(paths) ? paths[paths.length - 2].to : undefined;
    if (path) {
      // Restore query parameters from events page if available
      let queryParams = {};
      if (typeof window !== "undefined") {
        try {
          const storedQuery = sessionStorage.getItem(EVENTS_QUERY_STORAGE_KEY);
          if (storedQuery) {
            queryParams = JSON.parse(storedQuery);
            // Keep stored query params for future back navigations
            // They will be updated when user changes filters on events page
          }
        } catch (e) {
          // Ignore errors parsing stored query, but log for debugging
          console.error("Failed to parse stored events page query params:", e);
        }
      }

      // Prefer the events-page query snapshot so back navigation does not re-apply
      // breadcrumb filters (e.g. sport_type) after the user cleared them.
      const pathRoute = (typeof path === "object" ? path.path : path) || "/events";
      const pathQuery = typeof path === "object" && path.query ? path.query : {};
      const finalQuery = Object.keys(queryParams).length > 0 ? queryParams : pathQuery;

      router.push({
        path: pathRoute,
        query: finalQuery,
      });
    }
  };

  watch(priceRangeMax, (newVal) => {
    priceRange.value[1] = newVal;
  });

  /** Categories that are currently highlighted on the venue map */
  const highlightedCategories = ref<string[]>(selectedCategory.value ? [selectedCategory.value] : []);

  /** Exact category row hovered in the list (categoryId + subcategory) so map tooltip shows that ticket's price when hovering the list */
  const hoveredCategory = ref<{ categoryId: string; subcategory: string | undefined } | null>(null);

  const priceRange = ref<[number, number]>([0, priceRangeMax.value]);

  const resetFilters = () => {
    router.push({ query: {} });
    priceRange.value = [0, priceRangeMax.value];
  };

  const currentCategory = computed(() => {
    if (!selectedCategory.value) return undefined;
    return filteredCategories.value.find((cat) => cat.categoryId === selectedCategory.value);
  });

  return {
    isSingleDayEvent,
    isMultipleDays,
    backTo,
    event,
    eventsQuery,
    prefetchedTeams,
    prefetchedTeamsLoading,
    groupedCategories,
    categories: filteredCategories,
    allCategories,
    unfilteredCategories,
    categoriesBeforeTypeFilter,
    selectedCategoryType,
    selectedCategory,
    currentCategory,
    selectedSubcategory,
    guests,
    filterCount,
    perks,
    perkIconsFor,
    mapSelectedCategoryId,
    highlightedCategories,
    hoveredCategory,
    sort,
    filtersOpen,
    priceRange,
    priceRangeMax,
    resetFilters,
    fetchingComplete,
    totalTicketAmount,
    filterTicketsWithoutVat,
    showOnlyAvailableTicekts,
    shouldGroupBySupplier,
    availableDayDurations,
    selectedDayDurations,
    canShowDate,
    showTime,
  };
});

export { provideEventContext, useEventContext };
